import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Tile, EmptyStateMessage } from './Tile';
import { ContentType, shouldShowContent } from './contentTypes';
import { DocumentService, sharedApiClient } from '../../fetcher';
import { TaskTypeEnum } from '../../enums/taskTypeEnum';
import { UrgencyEnum, getUrgency } from '../../enums/urgencyEnum';
import { useAuth, useNotifications, CustomDropdown, getAssigneeName as resolveAssigneeName, createAssigneeOptions, getAssigneeIcon, outOfOfficeForAssignee } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import AddOutcomeTaskForm from '../AddOutcomeTaskForm';

const OutcomeTasksTab = ({ doc, activeUsers, teams = [], isRegenerationEnabled, isEditingEnabled, handleRegenerationClick, onDocumentUpdate, onPdfSearch }) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const hasDocsWriteAbility = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);
  const canEdit = isEditingEnabled ? isEditingEnabled() : hasDocsWriteAbility;
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editableTaskText, setEditableTaskText] = useState('');
  const [taskDropdownOpen, setTaskDropdownOpen] = useState({});
  const [taskTypeDropdownOpen, setTaskTypeDropdownOpen] = useState({});
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState({});
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);

  const taskTextareaRefs = useRef({});

  const handleTaskClick = useCallback((taskId, currentText) => {
    if (!canEdit) return;
    setEditingTaskId(taskId);
    setEditableTaskText(currentText);
  }, [canEdit]);

  const handleTaskBlur = useCallback((taskId, originalText) => {
    setEditingTaskId(null);
    // Auto-save if there are changes
    if (editableTaskText !== originalText && editableTaskText.trim()) {
      const updatedTasks = doc.outcomeTasks.map(task =>
        task.taskId === taskId ? { ...task, text: editableTaskText } : task
      );
      doc.outcomeTasks = updatedTasks;
      documentManager.updateDocumentTask(doc.documentId, taskId, { text: editableTaskText })
        .catch(error => {
          console.error('Error updating task text:', error);
          addNotification(`task-save-error-${taskId}`, 'Failed to save task changes', { isError: true });
        });
    }
    setEditableTaskText('');
  }, [editableTaskText, doc, documentManager, addNotification]);

  const handleTaskKeyDown = useCallback((e, taskId, originalText) => {
    if (e.key === 'Escape') {
      setEditableTaskText(originalText);
      setEditingTaskId(null);
    }
  }, []);

  // Focus textarea when editing starts
  useEffect(() => {
    if (editingTaskId && taskTextareaRefs.current[editingTaskId]) {
      taskTextareaRefs.current[editingTaskId].focus();
    }
  }, [editingTaskId]);

  const handleTaskTypeChange = useCallback((taskId, newTaskType) => {
    console.log(`Task ${taskId} type changed to ${newTaskType}`);
    // Update the task's type in the document
    const updatedTasks = doc.outcomeTasks.map(task =>
      task.taskId === taskId ? { ...task, taskType: newTaskType } : task
    );
    // Update the document state with the new task type
    doc.outcomeTasks = updatedTasks;
    // Call updateDocumentTask to persist the change
    documentManager.updateDocumentTask(doc.documentId, taskId, { taskType: newTaskType })
      .catch(error => {
        console.error('Error updating task type:', error);
        addNotification(`task-type-error-${taskId}`, 'Failed to update task type', { isError: true });
      });
    // Close the dropdown
    setTaskTypeDropdownOpen((prev) => ({
      ...prev,
      [taskId]: false,
    }));
  }, [doc, documentManager, addNotification]);

  const handleTaskAssigneeChange = useCallback((taskId, userId) => {
    console.log(`Task ${taskId} assigned to user ${userId}`);
    // Update the task's assignee in the document
    const updatedTasks = doc.outcomeTasks.map(task =>
      task.taskId === taskId ? { ...task, assigneeId: userId } : task
    );
    // Update the document state with the new assignee
    doc.outcomeTasks = updatedTasks;
    // Call updateDocumentTask to persist the change
    // Send '*' to backend when unassigning (userId is null)
    const userIdToSend = userId === null ? '*' : userId;
    documentManager.updateDocumentTask(doc.documentId, taskId, { assigneeId: userIdToSend })
      .catch(error => {
        console.error('Error updating task assignee:', error);
        addNotification(`task-assignee-error-${taskId}`, 'Failed to update task assignee', { isError: true });
      });
    // Close the dropdown
    setTaskDropdownOpen((prev) => ({
      ...prev,
      [taskId]: false,
    }));
  }, [doc, documentManager, addNotification]);

  const handleTaskUrgencyChange = useCallback((taskId, isUrgent) => {
    console.log(`Task ${taskId} urgency changed to ${isUrgent}`);
    // Update the task's urgency in the document
    const updatedTasks = doc.outcomeTasks.map(task =>
      task.taskId === taskId ? { ...task, isUrgent: isUrgent } : task
    );
    // Update the document state with the new urgency
    doc.outcomeTasks = updatedTasks;
    // Call updateDocumentTask to persist the change
    const urgencyUpdate = isUrgent ? { isUrgent: true } : { isNotUrgent: true };
    documentManager.updateDocumentTask(doc.documentId, taskId, urgencyUpdate)
      .catch(error => {
        console.error('Error updating task urgency:', error);
        addNotification(`task-urgency-error-${taskId}`, 'Failed to update task urgency', { isError: true });
      });
    // Close the dropdown
    setUrgencyDropdownOpen((prev) => ({
      ...prev,
      [taskId]: false,
    }));
  }, [doc, documentManager, addNotification]);

  // Handler for individual task selection change
  const handleTaskSelectionChange = useCallback((taskId, isChecked) => {
    if (!canEdit) return;
    console.log(`Task ${taskId} selection changed to ${isChecked}`);
    
    // Update local doc state
    const updatedTasks = doc.outcomeTasks.map(task =>
      task.taskId === taskId ? { ...task, isSelected: isChecked } : task
    );
    
    const updatedDoc = { ...doc, outcomeTasks: updatedTasks };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send the selection state to the backend
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    documentManager.updateDocumentTask(doc.documentId, taskId, { isSelected, isDeselected })
      .catch(error => {
        console.error('Error updating task selection:', error);
        addNotification(`task-select-error-${taskId}`, 'Failed to update task selection', { isError: true });
      });
  }, [canEdit, doc, onDocumentUpdate, documentManager, addNotification]);

  // Toggle functions for dropdowns
  const toggleTaskTypeDropdown = useCallback((taskId) => {
    setTaskTypeDropdownOpen((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);
  
  const toggleTaskDropdown = useCallback((taskId) => {
    setTaskDropdownOpen((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);

  const toggleUrgencyDropdown = useCallback((taskId) => {
    setUrgencyDropdownOpen((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);

  // Get assignee name
  const getAssigneeName = useCallback((userId) => {
    return resolveAssigneeName(userId, activeUsers, teams);
  }, [activeUsers, teams]);

  // Handler for selecting all outcome tasks
  const handleSelectAllTasks = useCallback((e) => {
    if (!canEdit) return;
    const checked = e.target.checked;
    
    // Update local doc state for all tasks
    const updatedTasks = doc.outcomeTasks.map(task => {
      return { ...task, isSelected: checked };
    });
    
    const updatedDoc = { ...doc, outcomeTasks: updatedTasks };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send selection state to backend for each task - no DOM manipulation needed
    doc.outcomeTasks.forEach((task) => {
      const isSelected = checked ? true : null;
      const isDeselected = !checked ? true : null;
      documentManager.updateDocumentTask(doc.documentId, task.taskId, { isSelected, isDeselected })
        .catch(error => {
          console.error(`Error updating task selection for ${task.taskId}:`, error);
          addNotification(`task-bulk-select-error-${task.taskId}`, 'Failed to update task selection', { isError: true });
        });
    });
  }, [canEdit, doc, onDocumentUpdate, documentManager, addNotification]);

  const handleAddTask = useCallback((newTask) => {
    documentManager.addOutcomeTasks(doc.documentId, [newTask])
      .then(() => {
        setShowAddTaskForm(false);
      })
      .catch(error => {
        console.error('Error adding task:', error);
        addNotification(`task-add-error-${doc.documentId}`, 'Failed to add task', { isError: true });
      });
  }, [doc.documentId, documentManager, addNotification]);

  // Render a single task item
  const renderTaskItem = (task) => {
    // Create task type options from the enum
    const taskTypeOptions = Object.keys(TaskTypeEnum)
      .map(typeKey => ({ 
        id: typeKey, 
        description: TaskTypeEnum[typeKey].description,
        icon: TaskTypeEnum[typeKey].icon
      }));

    // Create urgency options
    const urgencyOptions = [
      { 
        id: true, 
        description: UrgencyEnum.URGENT.description,
        icon: UrgencyEnum.URGENT.icon
      },
      { 
        id: false, 
        description: UrgencyEnum.NORMAL.description,
        icon: UrgencyEnum.NORMAL.icon
      }
    ];

    // Create assignee options with teams and users
    const assigneeOptions = createAssigneeOptions(activeUsers, teams);
    const selectedIcon = getAssigneeIcon(task.assigneeId, activeUsers, teams);
    
    // Get current urgency
    const currentUrgency = getUrgency(task.isUrgent || false);

    return (
      <div key={task.taskId} className="document-ai-item" id="ai-outcome-task-item">
        <div className="outcome-details">
          <div className="task-text-container">
            {editingTaskId === task.taskId ? (
              <textarea
                ref={(el) => taskTextareaRefs.current[task.taskId] = el}
                value={editableTaskText}
                onChange={(e) => setEditableTaskText(e.target.value)}
                onBlur={() => handleTaskBlur(task.taskId, task.text)}
                onKeyDown={(e) => handleTaskKeyDown(e, task.taskId, task.text)}
                className="task-edit-textarea"
                rows="3"
              />
            ) : (
              <div 
                className={`task-text ${canEdit ? 'task-text-editable' : ''}`}
                onClick={() => handleTaskClick(task.taskId, task.text)}
                onMouseMove={(e) => {
                  const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                  if (tooltip) {
                    tooltip.style.left = `${e.clientX + 10}px`;
                    tooltip.style.top = `${e.clientY + 10}px`;
                  }
                }}
              >
                {task.text || <em style={{ color: '#999' }}>No task description</em>}
                {canEdit && <div className="edit-tooltip">Click to edit task</div>}
              </div>
            )}
            {task.quote && (
              <div className="code-quote">
                <span
                  className={`code-quote-text${onPdfSearch ? ' clickable' : ''}`}
                  onClick={() => onPdfSearch && onPdfSearch(task.quote)}
                >
                  "{task.quote}"
                </span>
              </div>
            )}
          </div>

          <div className="custom-select-container-items">
            <div className="custom-select-container">
              <CustomDropdown
                selected={currentUrgency.description}
                selectedIcon={currentUrgency.icon}
                options={urgencyOptions}
                isOpen={urgencyDropdownOpen[task.taskId]}
                toggleDropdown={() => toggleUrgencyDropdown(task.taskId)}
                onSelect={(option) => handleTaskUrgencyChange(task.taskId, option.id)}
                disabled={!canEdit}
                selectedStyle={{ 
                  backgroundColor: currentUrgency.color,
                  color: '#000',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div className="custom-select-container">
              <CustomDropdown
                selected={TaskTypeEnum[task.taskType]?.description || 'Select Task Type'}
                selectedIcon={TaskTypeEnum[task.taskType]?.icon}
                options={taskTypeOptions}
                isOpen={taskTypeDropdownOpen[task.taskId]}
                toggleDropdown={() => toggleTaskTypeDropdown(task.taskId)}
                onSelect={(option) => handleTaskTypeChange(task.taskId, option.id)}
                disabled={!canEdit}
              />
            </div>
            {task.taskType === 'PATIENT_NOTE' ? (
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
                <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic', margin: 0 }}>
                  Patient message tasks are excluded when filing away
                </p>
              </div>
            ) : (
              <div className="custom-select-container">
                <CustomDropdown
                  searchable
                  searchPlaceholder="Search assignees..."
                  selected={getAssigneeName(task.assigneeId)}
                  selectedIcon={selectedIcon}
                  selectedOutOfOffice={outOfOfficeForAssignee(task.assigneeId, activeUsers)}
                  options={assigneeOptions}
                  isOpen={taskDropdownOpen[task.taskId]}
                  toggleDropdown={() => toggleTaskDropdown(task.taskId)}
                  onSelect={(assigneeOption) => handleTaskAssigneeChange(task.taskId, assigneeOption.userId)}
                  keyExtractor={(option) => option.id}
                  disabled={!canEdit}
                />
              </div>
            )}
          </div>
        </div>
        <input 
          type="checkbox" 
          className="outcome-checkbox" 
          id={`checkbox_${task.taskId}`} 
          checked={task.isSelected || false}
          onChange={(e) => handleTaskSelectionChange(task.taskId, e.target.checked)}
          disabled={!canEdit}
        />
      </div>
    );
  };

  // Create the tasks list content
  const renderTasksList = () => {
    const hasTasks = doc.outcomeTasks && doc.outcomeTasks.length > 0;

    return (
      <div className="outcome-tasks-list">
        {showAddTaskForm && (
          <AddOutcomeTaskForm
            activeUsers={activeUsers}
            teams={teams}
            onConfirm={handleAddTask}
            onCancel={() => setShowAddTaskForm(false)}
          />
        )}
        {hasTasks
          ? doc.outcomeTasks.map(renderTaskItem)
          : <EmptyStateMessage message="No outcome tasks found" />
        }
      </div>
    );
  };

  // Create the main tile
  const outcomeTasksTile = (
    <Tile
      doc={doc}
      documentId={doc.documentId} 
      contentType={ContentType.OUTCOME_STATUS} 
      title="Outcome Tasks"
      condition={shouldShowContent(doc.outcomeTasks, doc.status)}
      isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false} 
      onRegenerateClick={handleRegenerationClick}
      showContentOnError
      headerContent={
        doc.outcomeTasks && doc.outcomeTasks.length > 0 && canEdit &&
          <input 
          type="checkbox" 
          className="code-checkbox outcome-checkbox" 
          onChange={handleSelectAllTasks}
          disabled={!canEdit}
          />
      }
      preContent={
        <div className="tile-header-content">
          <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
            Patient message tasks are excluded when filing away.
          </p>
          <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic', marginTop: '-8px' }}>
            They can be used to send messages to the patient instead.
          </p>
        </div>
      }
    >
      {canEdit && !showAddTaskForm && (
        <button
          className="add-measurement-button"
          onClick={() => setShowAddTaskForm(true)}
        >
          + Add Task
        </button>
      )}
      {renderTasksList()}
    </Tile>
  );

  return (
    <div className="document-detail-tiles">
      {outcomeTasksTile}
    </div>
  );
};

export default OutcomeTasksTab; 